import { useQuery } from "@tanstack/react-query";
import { liteClient as algoliasearch } from "algoliasearch/lite";
import { useEffect } from "react";
import {
  Configure,
  InstantSearch,
  useInfiniteHits,
  useInstantSearch,
  useSearchBox,
} from "react-instantsearch";
import { TailSpin } from "react-loader-spinner";
import {
  Link,
  type LoaderFunction,
  useLoaderData,
  useNavigate,
  useSearchParams,
} from "react-router-dom";

import { Button } from "@/atoms";
import { useFacet } from "@/hooks/useFacet";
import type { AlgoliaSearchResult } from "@/models/AlgoliaSearchResult";
import { waitObj } from "@/toolbox/Promise";
import { defineLoader } from "@/toolbox/Router";
import { convertProductTypeToListingType } from "@/utils/productTypes";
import { get } from "api";
import type { Models } from "api/types";
import { Icon } from "atoms/icon/Icon";
import ChevronLeft from "atoms/icon/icons/chevron_left.svg";
import SearchError from "atoms/icon/icons/ic_search_error_XL_ol.svg";
import { SearchField } from "atoms/search-field/SearchField";
import useHistory from "hooks/useHistory";
import { ProductCardLarge } from "molecules/cards/product-card-large/ProductCardLarge";
import TopNav from "molecules/navbars/TopNav";
import { queryClient } from "pages/Root";
import { getListingsWithDiscountInformation } from "services/ListingHelperService";

const load = defineLoader(() => {
  const platform = queryClient.ensureQueryData(["/platform"], () =>
    get("/platform")
  );

  const shopData = queryClient.ensureQueryData({
    queryKey: ["/shop"],
    queryFn: () => get("/shop"),
  });

  const user = queryClient.ensureQueryData({
    queryKey: ["/user"],
    queryFn: () => get("/user"),
  });

  const recommendationIds = queryClient.fetchQuery(
    ["/adonis/top-recommendations/id"],
    () => get("/adonis/top-recommendations/id", { limit: 10 }),
    { staleTime: 1000 * 30 }
  );

  return waitObj({
    platform,
    shopData,
    user,
    recommendationIds,
  });
});

type LoaderData = Awaited<ReturnType<typeof load>>;

export const nextSearchLoader: LoaderFunction = (args) => {
  return load(args);
};

type DidntFindProps = {
  shopData?: Models<"ShopConfigModel">;
};

const DidntFindText = ({ shopData }: DidntFindProps) => {
  return (
    <div className="mt-2 text-center text-body2 text-secondary">
      Didn’t find what you were looking for?
      <br />
      Check your spelling or try{" "}
      <span className="font-bold text-brand-color">
        {shopData?.concerns && shopData.concerns.length > 0 ? (
          <Link to="/shop/concerns">Browse by concern</Link>
        ) : (
          <Link to="/shop">Browsing shop</Link>
        )}
      </span>
    </div>
  );
};

const CustomHits = ({
  user,
  shopDiscounts,
  customShopCategories,
  recommendationIds,
}: {
  user: Models<"UserModel1">;
  shopDiscounts?: Models<"ShopDiscountModel">;
  customShopCategories?: Models<"CategoryListingsResult">[];
  recommendationIds: string[];
}) => {
  const navigate = useNavigate();
  const { items, isLastPage, showMore, sendEvent } =
    useInfiniteHits<AlgoliaSearchResult>();

  const recommendedIds = new Set(recommendationIds);

  return (
    <>
      <ol className="flex list-none flex-col gap-4">
        {items.map((hit) => {
          const [enhancedHit] = getListingsWithDiscountInformation(
            [{ ...hit, id: hit.objectID }],
            shopDiscounts?.listingDiscounts,
            customShopCategories
          );

          const productType = convertProductTypeToListingType(
            enhancedHit.productType
          );

          const isProduct =
            productType === "Product" ||
            productType === "ProductFromExternalSource";

          const handleClick = () => {
            sendEvent("click", hit, "Hit Clicked");
            navigate(
              isProduct
                ? `/pdp/product/${enhancedHit.id}`
                : `/pdp/package/${enhancedHit.id}`
            );
          };

          return (
            <li key={hit.objectID} className="flex flex-col gap-4">
              <ProductCardLarge
                key={enhancedHit.id}
                title={enhancedHit.title}
                description={enhancedHit.description}
                imageUrl={enhancedHit.imageUrl}
                type={productType}
                tag=""
                concerns={enhancedHit.concernNames}
                price={enhancedHit.minBasePrice}
                memberPrice={null}
                onClick={handleClick}
                hasLinearPricing={enhancedHit.hasLinearPricing}
                unitType={enhancedHit.unitName}
                discount={enhancedHit.discountText}
                customColor={enhancedHit.customColor}
                guestMode={user?.isGuest}
                isRecommended={recommendedIds.has(enhancedHit.id)}
              />
            </li>
          );
        })}
      </ol>
      {!isLastPage && (
        <div className="mt-6">
          <Button fullWidth style="primary" onClick={showMore}>
            Load More
          </Button>
        </div>
      )}
    </>
  );
};

const AlgoliaSearchPage = () => {
  const { platform, shopData, user, recommendationIds } =
    useLoaderData() as LoaderData;
  const navigate = useNavigate();
  const { routeBackNumber } = useHistory();
  const [searchParams, setSearchParams] = useSearchParams();
  const { status } = useInstantSearch();
  const { items } = useInfiniteHits<AlgoliaSearchResult>();
  const { query, refine } = useSearchBox();
  const { refine: refinePlatformId } = useFacet({
    attribute: "platformId",
    limit: 1,
  });

  const { data: shopDiscounts } = useQuery({
    queryKey: ["/shop/discounts"],
    queryFn: () => get("/shop/discounts"),
    staleTime: 1000 * 10,
  });

  const { data: customShopCategories } = useQuery({
    queryKey: ["/shop/custom-categories/listings"],
    queryFn: () => get("/shop/custom-categories/listings"),
    staleTime: 1000 * 10,
  });

  useEffect(() => {
    refinePlatformId(platform.id);
  }, [platform, refinePlatformId]);

  useEffect(() => {
    const initialQuery = searchParams.get("q");

    if (initialQuery) {
      refine(initialQuery);
    }
  }, []);

  useEffect(() => {
    setSearchParams({ q: query }, { replace: true });
  }, [query, setSearchParams]);

  return (
    <>
      <TopNav
        startIconSVG={ChevronLeft}
        onStartIconClick={() => {
          navigate(routeBackNumber);
        }}
        showHeaderItems={false}
      >
        <div className="-m-4 -mt-2 mb-0 ml-0 flex w-full items-center">
          <SearchField
            autoFocus
            value={query}
            onChange={refine}
            onIconClick={(side) => {
              if (side === "right") {
                refine("");
              }
            }}
            placeholder="Search shop"
          />
        </div>
      </TopNav>
      <div className="flex h-full flex-col bg-background-two p-5">
        {status === "error" ? (
          <div className="my-auto flex items-center justify-center pb-16">
            <div className="text-center text-gray-500">
              <p>Sorry, something went wrong</p>
              <p className="mt-2 text-sm">Please try again later</p>
            </div>
          </div>
        ) : (
          <>
            {query &&
              !items.length &&
              (status === "loading" || status === "stalled") && (
                <div className="flex h-full items-center justify-center">
                  <TailSpin
                    height="36"
                    width="36"
                    radius="1"
                    color="rgb(var(--repeatmd-brand-color))"
                  />
                </div>
              )}
            {query && !!items.length && (
              <>
                <div className="mb-[30px] text-body2 text-secondary">
                  {items.length} results found
                </div>
                <div className="mb-6">
                  <div className="flex flex-col gap-4">
                    <CustomHits
                      user={user}
                      shopDiscounts={shopDiscounts}
                      customShopCategories={customShopCategories}
                      recommendationIds={recommendationIds}
                    />
                  </div>
                </div>
                <DidntFindText shopData={shopData} />
              </>
            )}
            {query && !items.length && (
              <div className="flex flex-col items-center pt-12">
                <div className="opacity-50">
                  <Icon svg={SearchError} size="large" color="darkGrey" />
                </div>
                <div className="mt-1 text-body1 text-primary">
                  No results found
                </div>
                <DidntFindText shopData={shopData} />
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};

const indexName = `${import.meta.env.REACT_APP_ALGOLIA_ENV}_search`;
const algoliaClient = algoliasearch(
  import.meta.env.REACT_APP_ALGOLIA_APP_ID,
  import.meta.env.REACT_APP_ALGOLIA_SEARCH_API_KEY
);

const NextSearchPage = () => {
  return (
    <InstantSearch insights searchClient={algoliaClient} indexName={indexName}>
      <Configure hitsPerPage={100} />
      <AlgoliaSearchPage />
    </InstantSearch>
  );
};

export default NextSearchPage;
